export const DOWNLOAD_ADAPTER_OPTIONS = [
  { adapter_id: "generic", label: "Generic" },
  { adapter_id: "godot", label: "Godot" },
  { adapter_id: "unity", label: "Unity" },
  { adapter_id: "gamemaker", label: "GameMaker" },
  { adapter_id: "phaser", label: "Phaser" },
] as const;

export type DownloadAdapterId = (typeof DOWNLOAD_ADAPTER_OPTIONS)[number]["adapter_id"];

export interface DownloadPackageRequest {
  product_id: string;
  product_version: string;
  adapter_id: DownloadAdapterId;
  asset_release_sha256: string;
}

export interface DownloadPackageValidation {
  ok: boolean;
  errors: string[];
  value?: DownloadPackageRequest;
}

export const DOWNLOAD_PACKAGE_FORMAT_VERSION = "1.0.0";
export const DOWNLOAD_COMPILER_VERSION = "1.0.0";

const PRODUCT_ID = /^[A-Za-z0-9._-]{3,160}$/;
const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;
const SHA256 = /^[a-f0-9]{64}$/;
const ADAPTER_IDS = new Set<string>(DOWNLOAD_ADAPTER_OPTIONS.map((option) => option.adapter_id));

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function validateDownloadPackageRequest(input: unknown): DownloadPackageValidation {
  if (!isRecord(input)) return { ok: false, errors: ["download package request must be an object"] };
  const errors: string[] = [];
  if ("adapter_ids" in input) errors.push("adapter_ids is not accepted; select exactly one adapter_id");

  const productId = typeof input.product_id === "string" ? input.product_id.trim() : "";
  const productVersion = typeof input.product_version === "string" ? input.product_version.trim() : "";
  const adapterId = typeof input.adapter_id === "string" ? input.adapter_id.trim() : "";
  const releaseSha = typeof input.asset_release_sha256 === "string" ? input.asset_release_sha256.trim() : "";

  if (!PRODUCT_ID.test(productId)) errors.push("product_id is invalid");
  if (!SEMVER.test(productVersion)) errors.push("product_version must be semantic versioning");
  if (!ADAPTER_IDS.has(adapterId)) errors.push("adapter_id is not supported by the website download contract");
  if (!SHA256.test(releaseSha)) errors.push("asset_release_sha256 must be lowercase SHA-256");

  if (errors.length) return { ok: false, errors };
  return {
    ok: true,
    errors: [],
    value: {
      product_id: productId,
      product_version: productVersion,
      adapter_id: adapterId as DownloadAdapterId,
      asset_release_sha256: releaseSha,
    },
  };
}

export interface DownloadPackageCacheIdentityInput {
  asset_release_sha256: string;
  adapter_id?: DownloadAdapterId;
  adapter_version: string;
  package_format_version?: string;
  compiler_version?: string;
  license_id?: string;
  license_version: string;
}

export function buildDownloadPackageCacheIdentity(input: DownloadPackageCacheIdentityInput) {
  const packageFormatVersion = input.package_format_version || DOWNLOAD_PACKAGE_FORMAT_VERSION;
  const compilerVersion = input.compiler_version || DOWNLOAD_COMPILER_VERSION;
  return [
    `asset_release_sha256=${input.asset_release_sha256}`,
    `adapter_id=${input.adapter_id || "legacy-unspecified"}`,
    `adapter_version=${input.adapter_version}`,
    `package_format_version=${packageFormatVersion}`,
    `compiler_version=${compilerVersion}`,
    `license_id=${input.license_id || "legacy-unspecified"}`,
    `license_version=${input.license_version}`,
  ].join("\n");
}

export function adapterLabel(adapterId: string) {
  return DOWNLOAD_ADAPTER_OPTIONS.find((option) => option.adapter_id === adapterId)?.label || adapterId;
}
